import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const AnimatedSection = ({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

const ContactPage = () => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  useSEO({
    title: "お問い合わせ | 決済管理システムの相談・導入検討",
    description: "Stream Solutions Limitedへのお問い合わせ。決済管理・資金管理システムの導入相談、サービス内容のご質問、デモのご依頼など、お気軽にお問い合わせください。1〜2営業日以内にご返答します。",
    keywords: "お問い合わせ,決済管理相談,フィンテック導入相談,Stream Solutions,システム導入",
    canonical: "/contact",
    ogTitle: "お問い合わせ | Stream Solutions Limited",
    ogDescription: "決済管理・資金管理システムの導入相談はこちら。専任担当者が1〜2営業日以内にご返答します。",
  });

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitState, setSubmitState] = useState<"idle" | "success" | "error">("idle");
  const [confirmData, setConfirmData] = useState<Record<string, string> | null>(null);
  const [payload, setPayload] = useState<URLSearchParams | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      setError("プライバシーポリシーへの同意が必要です。");
      return;
    }
    setError("");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    const preview: Record<string, string> = {};
    formData.forEach((value, key) => {
      const text = value.toString();
      data.append(key, text);
      preview[key] = text;
    });
    setPayload(data);
    setConfirmData(preview);
    setShowConfirm(true);
  };

  const handleConfirmedSend = async () => {
    if (!payload) return;

    setShowConfirm(false);
    setSubmitting(true);
    setSubmitState("idle");

    try {
      const res = await fetch("/send.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      if (res.ok && text.trim() === "OK") {
        formRef.current?.reset();
        setAgreed(false);
        setCharCount(0);
        setPayload(null);
        setConfirmData(null);
        setSubmitState("success");
      } else {
        setSubmitState("error");
      }
    } catch {
      setSubmitState("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract-communication-network-dark-background-with-glowing-connection-nodes-data-transmission-visualization-cyan-accent-lights-futuristic-digital-contact-concept-no-people-clean-minimal&width=1920&height=600&seq=contacthero001&orientation=landscape"
            alt="contact hero"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 to-slate-950" />
        </div>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-cyan-500" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">CONTACT</span>
            <span className="w-8 h-px bg-cyan-500" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">お問い合わせ</h1>
          <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            業務課題のご相談から導入のご検討まで、お気軽にお問い合わせください。
          </p>
        </div>
      </section>

      {/* Contact section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left: Info */}
            <AnimatedSection className="lg:col-span-2">
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">お気軽にご連絡ください</h2>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    ご質問・ご相談・デモのご依頼など、どんなことでもお気軽にお問い合わせください。専任担当者が1〜2営業日以内にご返答いたします。
                  </p>
                </div>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 flex-shrink-0">
                      <i className="ri-phone-line text-cyan-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">電話番号</div>
                      <a href="tel:+85253213455" className="text-slate-900 font-semibold text-sm hover:text-cyan-600 transition-colors cursor-pointer">
                        +852-5321-3455
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 flex-shrink-0">
                      <i className="ri-map-pin-line text-cyan-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">所在地</div>
                      <p className="text-slate-900 text-sm leading-relaxed">
                        Room D, Rm4. 2/F, Sunray Industrial Centre,<br />
                        610 Cha Kwo Ling Road, Yau Tong, KL.
                      </p>
                      <p className="text-slate-500 text-xs mt-1">
                        香港九龍油塘茶果嶺道610號 生利工業大廈2樓4室
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 flex-shrink-0">
                      <i className="ri-time-line text-cyan-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">対応時間</div>
                      <p className="text-slate-900 text-sm">平日 9:00〜18:00（HKT）</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right: Form */}
            <AnimatedSection delay={0.15} className="lg:col-span-3">
                <form
                  ref={formRef}
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-2">
                        お名前 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-2">
                        会社名
                      </label>
                      <input
                        type="text"
                        name="company"
                        placeholder="株式会社〇〇"
                        className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                      メールアドレス <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@company.com"
                      className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="03-0000-0000"
                      className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                      お問い合わせ種別
                    </label>
                    <select
                      name="inquiry_type"
                      className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200 cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="service">サービスについて</option>
                      <option value="system">システムについて</option>
                      <option value="demo">デモのご依頼</option>
                      <option value="price">料金について</option>
                      <option value="other">その他</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-2">
                      お問い合わせ内容 <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="ご質問・ご相談内容をご記入ください（500文字以内）"
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200 resize-none"
                    />
                    <div className="text-right text-xs text-slate-400 mt-1">{charCount}/500</div>
                  </div>

                  {/* Privacy checkbox */}
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <input type="hidden" name="privacy" value={agreed ? "同意する" : ""} />
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-cyan-500 cursor-pointer"
                      />
                      <span className="text-xs text-slate-600 leading-relaxed">
                        <a href="/privacy" className="text-cyan-600 hover:underline font-semibold" target="_blank" rel="noopener noreferrer">
                          プライバシーポリシー
                        </a>
                        に同意の上、送信します。
                        <span className="text-rose-500 ml-1">*</span>
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-xs">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !agreed}
                    className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
                  >
                    入力内容を確認する
                  </button>
                </form>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {showConfirm && confirmData && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-8 max-h-[85vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">入力内容の確認</h3>
            <p className="text-slate-500 text-sm mb-6">送信前に内容をご確認ください。</p>
            <div className="space-y-4 mb-8">
              {[
                ["お名前", confirmData.name],
                ["会社名", confirmData.company || "未入力"],
                ["メールアドレス", confirmData.email],
                ["電話番号", confirmData.phone || "未入力"],
                ["お問い合わせ種別", confirmData.inquiry_type || "未入力"],
                ["お問い合わせ内容", confirmData.message],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
                  <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">{value}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setShowConfirm(false)} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer">
                戻る
              </button>
              <button type="button" onClick={handleConfirmedSend} className="flex-1 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-400 transition-colors whitespace-nowrap cursor-pointer">
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-cyan-500"></i>
            <p className="mt-4 text-slate-900 font-semibold">送信中です...</p>
          </div>
        </div>
      )}

      {submitState !== "idle" && !submitting && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center">
            <i className={`${submitState === "success" ? "ri-checkbox-circle-fill text-green-500" : "ri-error-warning-fill text-rose-500"} text-5xl`}></i>
            <p className="mt-4 text-slate-900 font-semibold">
              {submitState === "success"
                ? "送信が完了しました。1〜2営業日以内に担当者よりご連絡いたします。"
                : "送信に失敗しました。しばらくしてから再度お試しください。"}
            </p>
            <button type="button" onClick={() => setSubmitState("idle")} className="mt-6 px-6 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-400 transition-colors whitespace-nowrap cursor-pointer">
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ContactPage;
